/* =====================================================================
   CONFIRM PROVIDER + useConfirm
   =====================================================================
   Promise-based imperative confirmation dialog.

   Mounts a single ConfirmDialog at the root of the app. Any component
   in the tree can await a confirmation without managing local state.

   Usage:
     const confirm = useConfirm();

     const ok = await confirm({
       title: "Supprimer ?",
       message: "Cette action est irréversible.",
       confirmLabel: "Supprimer",
       cancelLabel: "Annuler",
       variant: "destructive",   // "destructive" | "neutral"
     });

     if (!ok) return;
     // proceed
===================================================================== */

import { createContext, useCallback, useContext, useRef, useState } from "react";

import ConfirmDialog from "../../components/settings/shared/ConfirmDialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children, portalContainer = null }) {
  const [state, setState] = useState({ open: false, config: {} });
  const resolveRef = useRef(null);

  const confirm = useCallback((config) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, config: config ?? {} });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={state.open}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        portalContainer={portalContainer}
        {...state.config}
      />
    </ConfirmContext.Provider>
  );
}

/**
 * Returns an async `confirm(config)` function.
 * Resolves to `true` if the user confirmed, `false` if cancelled.
 * Must be used inside <ConfirmProvider>.
 */
export function useConfirm() {
  const confirm = useContext(ConfirmContext);

  if (!confirm) {
    throw new Error("useConfirm doit être utilisé dans ConfirmProvider");
  }

  return confirm;
}
